import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import type { ApiPageInitRequest, ApiPageInitResponse, PageInit } from '../page-init-api';
import type { ApiCreateTaskRequest } from '../create-task-api';
import type { ApiGetTaskDetailResponse } from '../get-task-detail-api';
import type { ApiClassificationHelperSearchRequest, ApiClassificationHelperSearchResponse, ClassificationHelperSearch } from '../classification-helper-search-api';
import type { ApiClassificationHelperSectionRequest, ApiClassificationHelperSectionResponse, ClassificationHelperSection } from '../classification-helper-section-api';
import type { ApiClassificationHelperSubclassRequest, ApiClassificationHelperSubclassResponse, ClassificationHelperSubclass } from '../classification-helper-subclass-api';
import type { ApiCreateAsyncTranslateTaskRequest, ApiCreateAsyncTranslateTaskResponse, CreateAsyncTranslateTaskApi } from '../create-async-translate-task-api';
import type { ApiUpdateTaskContentRequest, UpdateTaskContentApi } from '../update-task-content-api';
import type { ApiUpdateTaskNameRequest, UpdateTaskNameApi } from '../update-task-name-api';
import type { RecommendTranslateKeywordApi, RecommendTranslateKeywordRequest, RecommendTranslateKeywordResponse } from '../recommend-translate-keyword-api';
import type { UpdateTranslationApi, UpdateTranslationRequest, UpdateTranslationResponse } from '../update-translation-api';
import type { GetSpecificationClaimFeaturesApi, ApiGetSpecificationClaimFeaturesResponse, ApiGetSpecificationClaimFeaturesRequest } from '../get-specification-claim-features-api';
import type { GetEmbodimentKeywordsApi, ApiGetEmbodimentKeywordsResponse, ApiGetEmbodimentKeywordsRequest } from '../get-embodiment-keywords-api';
import type { EmbodimentAddApi, ApiEmbodimentAddApiResponse, ApiEmbodimentAddApiRequest } from '../embodiment-add-api';
import { FactoryPsHttpSSE, PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import type { ApiContentResponse, ApiGetContentRequest } from '../get-content-opt.api';
import type { AIDisclosureParagraphEditType } from '@/domains/disclosure/enums';
import { getCurrentLanguage } from '@telecom/shared/utils/language-tool';
import type { ApiRegenerateContentRequest, RegenerateContentApi } from '../regenerate-content-api';
import type { ApiPostFeedbackRequest, ApiPostFeedbackResponse, FeedbackApi } from '../feedback-api';
import type { DetectLanguageApi, DetectLanguageRequest, DetectLanguageResponse } from '../detect-language-api';
import type { TranslateTextApi, TranslateTextRequest, TranslateTextResponse } from '../translate-text-api';
import type { RestartTranslateTaskApi, RestartTranslateTaskRequest, RestartTranslateTaskResponse } from '../restart-translate-task-api';
import type { ContentHideApi, ContentHideApiRequest } from '../content-hide-api';
import type { ApiCreateSyncTranslateTaskRequest, ApiCreateSyncTranslateTaskResponse, CreateSyncTranslateTaskApi } from '../create-sync-translate-task-api';
import type { UploadGlossary } from '../upload-glossary-api';
import type { UpdateGlossary } from '../update-glossary-api';
import type { GetGlossary, Glossary } from '../get-glossary-api';
import type { TaskExportApi, TaskExportRequest, TaskExportResponse } from '../task-export-api';
import type { PatentProfileApi, PatentProfileRequest, PatentProfileResponse } from '../patent-profile-api';
import type { ApiCheckSensitiveWordsRequest, CheckSensitiveWords } from '../check-sensitive-words-api';
import type { ApiGetTechMeansRequest, ApiGetTechMeansResponse, GetTechMeans } from '../get-tech-means-api';
import type { ApiPutTechMeansRequest, ApiPutTechMeansResponse, PutTechMeans } from '../put-tech-means-api';
import type { ApiGetDisclosureTitleRequest, GetDisclosureTitle } from '../get-disclosure-title-api';
import type { ApiGetDisclosureContentRequest, ApiGetDisclosureContentResponse, GetDisclosureContent } from '../get-disclosure-content-api';
import type { ApiPutDisclosureSettingRequest, PutDisclosureSetting } from '../put-disclosure-setting-api';
import type { GetTaskContentApi, GetTaskContentResponse } from '../get-task-content-api';
import type { PatentDeepViewApi, PatentDeepViewSignatureRequest, PatentDeepViewSignatureResponse } from '../patent-deep-view-api';
import type {
    ApiGetMyUsageListRequest,
    ApiGetMyUsageListResponse,
    GetMyUsageList,
} from '../get-my-usage-list-api';

@injectable()
export class DraftingApiServiceImpl<T> extends PsHttpService
    implements
        PageInit,
        ClassificationHelperSearch,
        ClassificationHelperSection,
        ClassificationHelperSubclass,
        CreateAsyncTranslateTaskApi,
        CreateSyncTranslateTaskApi,
        RecommendTranslateKeywordApi,
        UpdateTaskContentApi,
        UpdateTranslationApi,
        UpdateTaskNameApi,
        RegenerateContentApi,
        FeedbackApi,
        ContentHideApi,
        DetectLanguageApi,
        TranslateTextApi,
        RestartTranslateTaskApi,
        UpdateTaskNameApi,
        GetSpecificationClaimFeaturesApi,
        GetEmbodimentKeywordsApi,
        EmbodimentAddApi,
        UploadGlossary,
        UpdateGlossary,
        GetGlossary,
        TaskExportApi,
        PatentProfileApi,
        UpdateTaskNameApi,
        CheckSensitiveWords,
        GetTechMeans,
        PutTechMeans,
        GetDisclosureTitle,
        GetDisclosureContent,
        PutDisclosureSetting,
        GetTaskContentApi,
        PatentDeepViewApi,
        GetMyUsageList
{
    async exportTask(data: TaskExportRequest): Promise<TaskExportResponse> {
        const res = await this.post<PatSnapSuccessResponseData<TaskExportResponse>>({
            url: 'ai/task/export',
            data,
        });
        return res.data;
    }

    async getPatentProfile(data: PatentProfileRequest):Promise<PatentProfileResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PatentProfileResponse>>({
            url: 'ai-drafting/patent/reference',
            data,
        });
        return res.data;
    }

    async restartTranslateTask(data: RestartTranslateTaskRequest): Promise<RestartTranslateTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<RestartTranslateTaskResponse>>({
            url: 'ai-translation/regenerate',
            data,
        });
        return res.data;
    }

    async detectLanguage(data: DetectLanguageRequest): Promise<DetectLanguageResponse> {
        const res = await this.post<PatSnapSuccessResponseData<DetectLanguageResponse>>({
            url: 'ai/task/lang-detect',
            data,
        });
        return res.data;
    }

    async translateText(data: TranslateTextRequest): Promise<TranslateTextResponse> {
        const res = await this.post<PatSnapSuccessResponseData<{ text: string }>>({
            url: 'translation/text',
            data,
        });
        return res.data;
    }

    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    async pageInit(data: ApiPageInitRequest) {
        const res = await this.get<PatSnapSuccessResponseData<ApiPageInitResponse>>({
            url: 'ai-drafting/init',
            data,
            uuid: 'ai-drafting/init',
        });
        return res.data;
    }

    /**
     * 创建任务(交底书/翻译/说明书)
     * @param data
     */
    async createTask(data: ApiCreateTaskRequest<T>) {
        const res = await this.post<PatSnapSuccessResponseData<void>>({
            url: 'ai/task',
            data,
        });
        return res.data;
    }

    async getTaskDetail(taskId: string) {
        const res = await this.get<PatSnapSuccessResponseData<ApiGetTaskDetailResponse<T>>>({
            url: `ai/task/${taskId}`,
        });
        return res.data;
    }

    async classificationHelperSearch(params: ApiClassificationHelperSearchRequest) {
        const res = await this.get<PatSnapSuccessResponseData<ApiClassificationHelperSearchResponse>>({
            url: 'ai-specification/classification/helper-search',
            params,
        });
        return res.data;
    }

    async createAsyncTranslateTask(data: ApiCreateAsyncTranslateTaskRequest): Promise<ApiCreateAsyncTranslateTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiCreateAsyncTranslateTaskResponse>>({
            url: 'ai-translation/translation/async',
            data,
        });
        return res.data;
    }

    async classificationHelperSection(params: ApiClassificationHelperSectionRequest) {
        const res = await this.get<PatSnapSuccessResponseData<ApiClassificationHelperSectionResponse>>({
            url: 'ai-specification/classification/helper-section',
            params,
        });
        return res.data;
    }

    async createSyncTranslateTask(data: ApiCreateSyncTranslateTaskRequest): Promise<ApiCreateSyncTranslateTaskResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiCreateSyncTranslateTaskResponse>>({
            url: 'ai-translation/task',
            data,
        });
        return res.data;
    }

    async updateTaskContent(data: ApiUpdateTaskContentRequest<string>): Promise<void> {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: `ai/task/${data.taskId}/content/${data.contentType}`,
            data: {
                content: data.content,
            },
        });
        return res.data;
    }

    async getTaskContent(taskId: string, contentType: string): Promise<GetTaskContentResponse> {
        const res = await this.get<PatSnapSuccessResponseData<GetTaskContentResponse>>({
            url: `ai/task/content/${taskId}/${contentType}`,
        });
        return res.data;
    }

    async classificationHelperSubclass(params: ApiClassificationHelperSubclassRequest) {
        const res = await this.get<PatSnapSuccessResponseData<ApiClassificationHelperSubclassResponse>>({
            url: '/ai-specification/classification/helper-subclass',
            params,
        });
        return res.data;
    }

    async updateTaskName(data: ApiUpdateTaskNameRequest): Promise<void> {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: `ai/task/${data.taskId}/title`,
            data: {
                content: data.content,
            },
        });
        return res.data;
    }

    async getRecommendTranslateKeyword(request: RecommendTranslateKeywordRequest): Promise<RecommendTranslateKeywordResponse> {
        const res = await this.get<PatSnapSuccessResponseData<RecommendTranslateKeywordResponse>>({
            url: `ai-translation/term/${request.taskId}`,
        });
        return res.data;
    }

    async updateTranslation(data: UpdateTranslationRequest): Promise<UpdateTranslationResponse> {
        const res = await this.put<PatSnapSuccessResponseData<UpdateTranslationResponse>>({
            url: 'ai-translation/translation',
            data,
        });
        return res.data;
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    getContentOpt(data: ApiGetContentRequest<AIDisclosureParagraphEditType>) {
        const sse = this._factorySsePsHttp<ApiContentResponse>().init({
            url: `${coreTelecomApiUrlPrefix}/patent-disclosure/content/opt`,
            data,
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    async regenerateContent(data: ApiRegenerateContentRequest): Promise<void> {
        const res = await this.post<PatSnapSuccessResponseData<void>>({
            url: `${coreTelecomApiUrlPrefix}/patent-disclosure/content/regenerate`,
            data,
        });
        return res.data;
    }

    async postFeedback(data: ApiPostFeedbackRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiPostFeedbackResponse>>({
            url: 'feedback/submit',
            data,
        });
        return res.data;
    }

    async contentHideApi(data: ContentHideApiRequest) {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: `ai/task/${data.task_id}/content/${data.content_type}/content-hide`,
            data: {
                hide: data.hidden,
            },
        });
        return res.data;
    }

    getSpecificationClaimFeatures(data: ApiGetSpecificationClaimFeaturesRequest) {
        const sse = this._factorySsePsHttp<ApiGetSpecificationClaimFeaturesResponse>().init({
            url: `${coreTelecomApiUrlPrefix}/ai-specification/content`,
            data: {
                task_id: data.task_id,
                content_type: 'feature_tree',
                current_step: 'CLAIM_PARSER',
            },
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    embodimentAdd(data: ApiEmbodimentAddApiRequest) {
        const sse = this._factorySsePsHttp<ApiEmbodimentAddApiResponse>().init({
            url: `${coreTelecomApiUrlPrefix}/ai-specification/embodiment/add`,
            data,
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    async getEmbodimentKeywords(data: ApiGetEmbodimentKeywordsRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetEmbodimentKeywordsResponse>>({
            url: '/ai-specification/embodiment/keywords',
            data,
        });
        return res.data;
    }

    async uploadGlossary(params: File): Promise<void> {
        const formData = new FormData();
        formData.append('file', params);
        const res = await this.post<PatSnapSuccessResponseData<void>>({
            url: 'ai-translation/term/import',
            data: formData,
        });
        return res.data;
    }

    async updateGlossary(params: Glossary): Promise<void> {
        const res = await this.post<PatSnapSuccessResponseData<void>>({
            url: 'ai-translation/term',
            data: params,
        });
        return res.data;
    }

    async getGlossary(): Promise<Glossary> {
        const res = await this.get<PatSnapSuccessResponseData<Glossary>>({
            url: 'ai-translation/term/custom',
        });
        return res.data;
    }

    async checkSensitiveWords(data: ApiCheckSensitiveWordsRequest): Promise<string> {
        const res = await this.post<PatSnapSuccessResponseData<string>>({
            url: '/ai-drafting/sensitive-word',
            data,
        });
        return res.data;
    }


    async getTechMeans(data: ApiGetTechMeansRequest) {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetTechMeansResponse>>({
            url: '/patent-disclosure/tech-means',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async putTechMeans(data: ApiPutTechMeansRequest) {
        const res = await this.put<PatSnapSuccessResponseData<ApiPutTechMeansResponse>>({
            url: '/patent-disclosure/tech-means',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getDisclosureTitle(data: ApiGetDisclosureTitleRequest) {
        const res = await this.post<PatSnapSuccessResponseData<string>>({
            url: '/patent-disclosure/title',
            data,
            transformCode: true,
        });
        return res.data;
    }

    getDisclosureContent(data: ApiGetDisclosureContentRequest<any>): PsHttpSSE<ApiGetDisclosureContentResponse> {
        const sse = this._factorySsePsHttp<ApiGetDisclosureContentResponse>().init({
            baseURL: coreTelecomApiUrlPrefix,
            url: '/patent-disclosure/content',
            data,
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }

    async putDisclosureSetting(data: ApiPutDisclosureSettingRequest) {
        const res = await this.put<PatSnapSuccessResponseData<void>>({
            url: '/patent-disclosure/setting',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getPatentDeepViewSignature(data: PatentDeepViewSignatureRequest): Promise<PatentDeepViewSignatureResponse> {
        const res = await this.get<PatSnapSuccessResponseData<{signed_result: PatentDeepViewSignatureResponse[]}>>({
            url: `/patent/${data.patent_id}/signature`,
        });
        return res.data.signed_result[0];
    }

    async getMyUsageList(data: ApiGetMyUsageListRequest): Promise<ApiGetMyUsageListResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ApiGetMyUsageListResponse>>({
            url: 'unified-usage-detail/my-list',
            data,
        });
        return res.data;
    }
}
